import { useState, useEffect, useContext } from 'react';
import { buildingsAPI } from '../services/api';
import { AuthContext } from '../context/AuthContext';

/**
 * Buildings of the active tenant plus CRUD actions. Pass `enabled: false` to
 * skip the request (e.g. the caller lacks building:read); the hook then
 * reports no buildings and not loading, and fetches as soon as it is enabled.
 */
export const useBuildings = ({ enabled = true } = {}) => {
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (!enabled) {
      // Re-enabling (e.g. after a tenant switch) must show a load, not the
      // previous tenant's buildings.
      setLoading(true);
      return;
    }
    if (user) {
      fetchBuildings();
    } else {
      setBuildings([]);
      setLoading(false);
    }
  }, [user, enabled]);

  const fetchBuildings = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await buildingsAPI.getBuildings();
      setBuildings(data || []);
    } catch (err) {
      console.error('Error fetching buildings:', err);
      setError(err.message);
      setBuildings([]);
    } finally {
      setLoading(false);
    }
  };

  const addBuilding = async (buildingData) => {
    try {
      setError(null);

      const data = await buildingsAPI.createBuilding(buildingData);
      setBuildings([data, ...buildings]);
      return { success: true, data };
    } catch (err) {
      console.error('Error adding building:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const updateBuilding = async (id, updates) => {
    try {
      setError(null);

      const data = await buildingsAPI.updateBuilding(id, updates);
      setBuildings(buildings.map((b) => (b.id === id ? data : b)));
      return { success: true, data };
    } catch (err) {
      console.error('Error updating building:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const deleteBuilding = async (id) => {
    try {
      setError(null);

      await buildingsAPI.deleteBuilding(id);
      setBuildings(buildings.filter((b) => b.id !== id));
      return { success: true };
    } catch (err) {
      console.error('Error deleting building:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  return {
    buildings: enabled ? buildings : [],
    loading: enabled ? loading : false,
    error,
    addBuilding,
    updateBuilding,
    deleteBuilding,
    refetch: fetchBuildings,
  };
};
